import React, { useState } from 'react';
import { DSButton, DSCard, DSIcon, DSModal } from '../design-system/primitives';
import '../pages/pingolfShared.css';

// PinGolf's headline numbers, where every one of them can explain itself on tap. Handicaps,
// net scoring, dropped weeks and the two prize pools are the parts players ask about, and the
// numbers alone never say enough - so the explainer travels with the stat rather than living
// in fine print beside it.
//
// Two presentations, same data and same modal:
//   tiles   (default) icon + label + value cards in a wrapping grid. The general case.
//   compact a bare value-over-label row, for a dense header like the scorecard's.
//
// Stat shape: { key, label, value, icon, tone, detailTitle, detailBody, detailBreakdown,
// detailFootnote }. `value` and `detailBody` may be nodes. `detailBreakdown` rows are
// { key, label, note, amount } and render as a right-aligned money list.
function PinGolfStats({ stats, variant = 'tiles', className = '', ariaLabel }) {
  const [statDetail, setStatDetail] = useState(null);

  if (!stats || stats.length === 0) return null;

  const isCompact = variant === 'compact';
  const wrapperClass = [isCompact ? 'pingolf-stat-compact-row' : 'pingolf-stat-tiles', className]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={wrapperClass} aria-label={ariaLabel}>
        {stats.map((stat) => (
          <button
            type="button"
            key={stat.key}
            className={isCompact ? 'pingolf-stat-compact' : 'pingolf-stat-tile'}
            onClick={() => setStatDetail(stat)}
            aria-label={`${stat.label}. Tap for details.`}
          >
            {isCompact ? (
              <>
                <span className="pingolf-stat-compact-value">{stat.value}</span>
                <span className="pingolf-stat-compact-label">
                  {stat.label}
                  <span className="pingolf-stat-hint" aria-hidden="true">
                    <DSIcon name="info" size="sm" tone="muted" />
                  </span>
                </span>
              </>
            ) : (
              <DSCard variant="outlined" bgVariant="subtle" borderVariant="subtle" cornerVariant="lg">
                <div className="pingolf-stat-tile-body">
                  {stat.icon && (
                    <span className="pingolf-stat-tile-icon" aria-hidden="true">
                      <DSIcon name={stat.icon} size="md" tone={stat.tone} />
                    </span>
                  )}
                  <span className="pingolf-stat-tile-text">
                    <span className="pingolf-stat-tile-label">{stat.label}</span>
                    <strong>{stat.value}</strong>
                  </span>
                  <span className="pingolf-stat-hint" aria-hidden="true">
                    <DSIcon name="info" size="sm" tone="muted" />
                  </span>
                </div>
              </DSCard>
            )}
          </button>
        ))}
      </div>

      {statDetail && (
        <DSModal title={statDetail.detailTitle} size="md" onClose={() => setStatDetail(null)}>
          <div className="pingolf-stat-detail-value">{statDetail.value}</div>
          <div className="pingolf-stat-detail-body">{statDetail.detailBody}</div>
          {statDetail.detailBreakdown?.length > 0 && (
            <ul className="pingolf-stat-detail-breakdown">
              {statDetail.detailBreakdown.map((row) => (
                <li key={row.key || row.label}>
                  <span className="pingolf-stat-detail-place">{row.label}</span>
                  {row.note && <span className="pingolf-stat-detail-share">{row.note}</span>}
                  <strong className="pingolf-stat-detail-amount">{row.amount}</strong>
                </li>
              ))}
            </ul>
          )}
          {statDetail.detailFootnote && (
            <p className="pingolf-stat-detail-footnote">{statDetail.detailFootnote}</p>
          )}
          <div className="form-actions">
            <DSButton appearance="outline" tone="neutral" onClick={() => setStatDetail(null)}>Close</DSButton>
          </div>
        </DSModal>
      )}
    </>
  );
}

export default PinGolfStats;
